import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft, ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";

export default function CaseReport() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`/cases/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);
  if (!c) return <div className="p-10 text-muted-foreground">{t("loading")}</div>;
  const a = c.ai_analysis || c.advisory_note || {};
  const refs = a.regulatory_references || [];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto p-4 print:hidden flex justify-between items-center">
        <Button variant="ghost" onClick={() => nav(`/app/cases/${id}`)} className="rounded-full"><ArrowLeft className="h-4 w-4 me-1" /> {t("back")}</Button>
        <Button onClick={() => window.print()} className="rounded-full bg-[hsl(217_63%_15%)]"><Printer className="h-4 w-4 me-1.5" /> {t("print_report")}</Button>
      </div>
      <div className="max-w-3xl mx-auto p-8 sm:p-12" data-testid="case-report">
        <div className="flex items-center justify-between border-b-2 border-[hsl(217_63%_15%)] pb-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[hsl(217_63%_15%)] flex items-center justify-center"><ShieldCheck className="h-5 w-5" style={{ color: "hsl(41 72% 55%)" }} /></div>
            <div><div className="font-display font-extrabold text-[hsl(217_63%_15%)]">VATRdar</div><div className="text-[10px] text-muted-foreground">A Growth Curve Product</div></div>
          </div>
          <div className="text-end text-xs text-muted-foreground"><div className="font-mono font-bold text-[hsl(158_64%_28%)]">{c.reference}</div><div>{(c.created_at || "").slice(0, 10)}</div></div>
        </div>

        <h1 className="mt-6 font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("report_title")}</h1>
        <div className="mt-1 text-sm text-muted-foreground">{lang === "ar" ? c.service_label_ar : c.service_label_en} · {c.organization_name}</div>

        {(a.client_summary_en || a.client_summary_ar) && (
          <section className="mt-6">
            <h2 className="font-semibold text-[hsl(217_63%_15%)] border-b border-border pb-1">{t("exec_summary")}</h2>
            <p className="mt-2 text-sm leading-relaxed">{lang === "ar" ? a.client_summary_ar : a.client_summary_en}</p>
          </section>
        )}

        {(c.findings || []).length > 0 && (
          <section className="mt-6">
            <h2 className="font-semibold text-[hsl(217_63%_15%)] border-b border-border pb-1">{t("findings")}</h2>
            <table className="w-full text-sm mt-2">
              <thead><tr className="text-xs text-muted-foreground"><th className="text-start py-1">{t("category")}</th><th className="text-start py-1">{t("risk_level")}</th><th className="text-start py-1">{t("sources")}</th></tr></thead>
              <tbody>
                {c.findings.map((f) => (
                  <tr key={f.id} className="border-t border-border align-top">
                    <td className="py-2 pe-2"><b>{f.category}</b><div className="text-xs text-muted-foreground">{lang === "ar" ? f.description_ar : f.description_en}</div></td>
                    <td className="py-2 pe-2 uppercase text-xs">{f.risk_level}</td>
                    <td className="py-2 text-xs font-mono text-[hsl(158_64%_28%)]">{f.source || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {refs.length > 0 && (
          <section className="mt-6">
            <h2 className="font-semibold text-[hsl(217_63%_15%)] border-b border-border pb-1">{t("regulatory_refs")}</h2>
            <ul className="mt-2 text-sm space-y-1">
              {refs.map((r, i) => <li key={i}><span className="font-mono font-semibold text-[hsl(158_64%_28%)]">{r.article}</span> — {lang === "ar" ? r.title_ar : r.title_en}</li>)}
            </ul>
          </section>
        )}

        <section className="mt-6 grid grid-cols-3 gap-3 text-xs">
          <div><div className="text-muted-foreground">{t("prepared_by")}</div><div className="font-medium">{c.prepared_by || "—"}</div></div>
          <div><div className="text-muted-foreground">{t("reviewed_by")}</div><div className="font-medium">{c.reviewed_by || "—"}</div></div>
          <div><div className="text-muted-foreground">{t("quality_approved_by")}</div><div className="font-medium">{c.quality_approved_by || "—"}</div></div>
        </section>

        <p className="mt-8 text-[10px] text-muted-foreground leading-relaxed border-t border-border pt-3">{t("disclaimer_note")}</p>
        <p className="mt-2 text-[10px] text-muted-foreground">© Growth Curve Professional Consultancy</p>
      </div>
    </div>
  );
}
